import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { GameState } from '../systems/GameState';
import { CharacterAnimator } from '../systems/CharacterAnimator';
import { getCharacterTextureKey } from '../systems/CharacterSprite';
import { OUTFITS_BY_ID, OUTFITS_BY_SLOT } from '../data/outfits';
import { svgStringToTexture } from '../utils/svgToTexture';
import { rivalForToday } from '../data/rivals';
import { OutfitIconRenderer } from '../systems/OutfitIconRenderer';
import { Button } from '../ui/Button';
import { ToastManager } from '../ui/ToastManager';
import { EconomyEngine } from '../systems/EconomyEngine';
import { EventBus } from '../systems/EventBus';
import { AudioManager } from '../systems/AudioManager';
import type { OutfitSlot, Theme, HairStyle } from '../types';

const SLOTS: OutfitSlot[] = ['top','bottom','hair','hairpin','glasses','bag','shoes'];

const THEME_LABELS: Record<Theme, { name: string; emoji: string }> = {
  casual:{name:'캐주얼',emoji:'😎'}, party:{name:'파티',emoji:'🎉'}, beach:{name:'비치',emoji:'🏖️'},
  school:{name:'학교',emoji:'🎓'}, sport:{name:'스포츠',emoji:'⚽'}, formal:{name:'포멀',emoji:'🎩'},
  holiday:{name:'홀리데이',emoji:'🎄'}, fantasy:{name:'판타지',emoji:'🧚'},
};

export class RunwayScene extends Phaser.Scene {
  private rival = rivalForToday();
  private mySprite: Phaser.GameObjects.Image | null = null;
  private rivalSprite: Phaser.GameObjects.Image | null = null;
  private toast!: ToastManager;
  private originalEquipped: Partial<Record<OutfitSlot, string>> = {};
  private trialEquipped: Partial<Record<OutfitSlot, string>> = {};
  private currentSlot: OutfitSlot = 'top';
  private gridContainer: Phaser.GameObjects.Container | null = null;
  private tabContainer: Phaser.GameObjects.Container | null = null;
  private myScoreText!: Phaser.GameObjects.Text;
  private rivalScoreText!: Phaser.GameObjects.Text;
  private isBattling = false;

  constructor() { super('RunwayScene'); }

  async create(): Promise<void> {
    try {
      AudioManager.setScene(this);
      AudioManager.playBgm('runway');
      this.scene.launch('HUDScene');  // HUD 표시 (별/코인 보이게)
      this.cameras.main.setBackgroundColor('#1a0f2e');
    if (this.textures.exists('bg_runway')) {
      const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_runway');
      // 화면을 채우도록 스케일 — cover 방식
      const sx = GAME_WIDTH / bg.width;
      const sy = GAME_HEIGHT / bg.height;
      const s = Math.max(sx, sy);
      bg.setScale(s).setDepth(-100);
      // 가독성용 살짝 톤 다운
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x1a0f2e, 0.3).setDepth(-99);
    }
      this.cameras.main.fadeIn(300);
      this.toast = new ToastManager(this);
      this.rival = rivalForToday();
      this.originalEquipped = { ...GameState.data.currentEquipped };
      this.trialEquipped = { ...GameState.data.currentEquipped };
      this.isBattling = false;

      // 무대 (런웨이)
      this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT-180, GAME_WIDTH, 240, 0x6a3a8a);
      // Stage spotlights
      const spotL = this.add.circle(280, GAME_HEIGHT/2 + 20, 180, 0xffffff, 0.04);
      const spotR = this.add.circle(GAME_WIDTH-280, GAME_HEIGHT/2 + 20, 180, 0xffffff, 0.04);
      this.tweens.add({ targets: [spotL, spotR], alpha: 0.01, duration: 2000, yoyo: true, repeat: -1, ease: 'Sine.InOut' });

      // 타이틀
      this.add.text(GAME_WIDTH/2, 50, '💃 오늘의 라이벌 패션 대결', { fontFamily: 'Pretendard Variable', fontSize: '24px', fontStyle: '800', color: '#fff' }).setOrigin(0.5);
      this.add.text(GAME_WIDTH/2, 90, `${this.rival.name}  난이도: ${this._difficultyStars()}  테마: ${THEME_LABELS[this.rival.preferredTheme].emoji} ${THEME_LABELS[this.rival.preferredTheme].name}`, { fontFamily: 'Pretendard Variable', fontSize: '18px', fontStyle: '700', color: '#ffd95c' }).setOrigin(0.5);

      // 점수 표시 (먼저 만들어서 await 중 reference 안전)
      this.myScoreText = this.add.text(280, 200, '로하: ⭐ 0', { fontFamily: 'Pretendard Variable', fontSize: '18px', fontStyle: '900', color: '#ffd95c' }).setOrigin(0.5);
      this.rivalScoreText = this.add.text(GAME_WIDTH-280, 200, `${this.rival.name}: ⭐ 0`, { fontFamily: 'Pretendard Variable', fontSize: '18px', fontStyle: '900', color: '#ffd95c' }).setOrigin(0.5);

      // 우측: 옷장 미니 (await 전에 먼저)
      this.renderTabs();
      this.renderGrid();
      this.updateScores();

      // 액션 버튼 (가장 중요 — await 전에 먼저 만들어서 무조건 보이게)
      new Button(this, 100, GAME_HEIGHT-50, '← 돌아가기', () => this.abort(), { width: 140, height: 48, style: 'secondary' });
      new Button(this, GAME_WIDTH-160, GAME_HEIGHT-50, '✨ 대결 시작!', () => void this.battle(), { width: 200, height: 56, style: 'gold' });

      // 캐릭터 (async — 마지막에. 실패해도 다른 UI는 작동)
      await this.refreshCharacters().catch(e => console.warn('refreshCharacters failed:', e));
    } catch (e) {
      console.error('RunwayScene.create error:', e);
      // 에러나도 최소한 돌아가기는 가능하게
      new Button(this, GAME_WIDTH/2, GAME_HEIGHT/2, '← 홈', () => this.abort(), { width: 200, height: 60, style: 'primary' });
    }
  }

  /** AudioManager가 SFX를 갖고 있지 않더라도 게임이 멈추지 않게 */
  private _safeSfx(name: 'win'|'lose'|'click'): void {
    try { AudioManager.playSfx(name); } catch (e) { console.warn('sfx failed:', e); }
  }

  private async refreshCharacters(): Promise<void> {
    // 내 캐릭터 — char_full PNG 직접 사용 (게임 내 다른 화면과 동일)
    if (this.mySprite) { CharacterAnimator.reset(this, this.mySprite); this.mySprite.destroy(); }
    const myKey = getCharacterTextureKey(this, this.trialEquipped);
    this.mySprite = this.add.image(280, GAME_HEIGHT/2 + 20, myKey).setScale(0.9);
    if (!this.isBattling) CharacterAnimator.idle(this, this.mySprite);

    // 라이벌
    if (this.rivalSprite) { CharacterAnimator.reset(this, this.rivalSprite); this.rivalSprite.destroy(); }
    const rEqMap: Partial<Record<OutfitSlot, string>> = {};
    for (const id of this.rival.outfitLoadout) {
      const o = OUTFITS_BY_ID[id];
      if (o) rEqMap[o.slot] = id;
    }
    const rKey = getCharacterTextureKey(this, rEqMap);
    this.rivalSprite = this.add.image(GAME_WIDTH-280, GAME_HEIGHT/2 + 20, rKey).setScale(0.9);
    if (!this.isBattling) CharacterAnimator.idle(this, this.rivalSprite);
  }

  private renderTabs(): void {
    if (this.tabContainer) this.tabContainer.destroy();
    this.tabContainer = this.add.container(0,0);
    SLOTS.forEach((slot, i) => {
      const x = 600 + (i % 4) * 100;
      const y = 200 + Math.floor(i/4) * 50;
      const bg = this.add.rectangle(x, y, 90, 40, slot === this.currentSlot ? 0xff6fa1 : 0x3a2540);
      bg.setStrokeStyle(2, 0xffabd0);
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerdown', () => { this.currentSlot = slot; this.renderTabs(); this.renderGrid(); });
      const label = this.add.text(x, y, slot, { fontFamily: 'Pretendard Variable', fontSize: '12px', fontStyle: '700', color: '#fff' }).setOrigin(0.5);
      this.tabContainer!.add([bg, label]);
    });
  }

  private renderGrid(): void {
    if (this.gridContainer) this.gridContainer.destroy();
    this.gridContainer = this.add.container(0,0);
    const items = OUTFITS_BY_SLOT[this.currentSlot] ?? [];
    const ownedIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));

    items.forEach((it, i) => {
      const x = 620 + (i % 3) * 100;
      const y = 340 + Math.floor(i / 3) * 100;
      const owned = ownedIds.has(it.id);
      const equipped = this.trialEquipped[this.currentSlot] === it.id;
      const sw = this.add.rectangle(x, y, 80, 80, owned ? parseInt(it.color.slice(1), 16) : 0x222222);
      sw.setStrokeStyle(equipped ? 4 : 2, equipped ? 0xffd95c : 0xffabd0);
      this.gridContainer!.add(sw);

      // Outfit icon overlay
      const hairStyle = (it.slot === 'hair' && it.hairStyle) ? it.hairStyle as HairStyle : undefined;
      const iconSvg = OutfitIconRenderer.render(it.slot, it.color, hairStyle, it.iconStyle);
      const iconKey = `runway_grid_${it.id}`;
      void svgStringToTexture(this, iconKey, iconSvg).then(() => {
        if (!this.scene.isActive() || !this.gridContainer) return;
        const icon = this.add.image(x, y - 5, iconKey).setScale(1.8);
        if (!owned) icon.setAlpha(0.35);
        this.gridContainer!.add(icon);
      });

      if (owned) {
        sw.setInteractive({ useHandCursor: true });
        sw.on('pointerdown', async () => {
          this.trialEquipped[this.currentSlot] = it.id;
          await this.refreshCharacters();
          this.renderGrid();
          this.updateScores();
        });
      }
    });
  }

  private calcScore(equipped: Partial<Record<OutfitSlot, string>>, isRival = false): number {
    let total = 0;
    for (const slot of SLOTS) {
      const id = equipped[slot]; if (!id) continue;
      const o = OUTFITS_BY_ID[id]; if (!o) continue;
      if (o.themes.includes(this.rival.preferredTheme)) total += 25;
      else total += 5;
      if (o.rarity === 'rare') total += 5;
      if (o.rarity === 'epic') total += 10;
      if (o.rarity === 'legend') total += 20;
    }
    if (isRival) {
      // 라이벌 점수를 난이도별로 하향 조정 (로하가 이길 수 있도록)
      // difficulty 1 → ×0.50, 2 → ×0.60, 3 → ×0.72, 4 → ×0.84, 5 → ×0.92
      const diffMultiplier = [0, 0.50, 0.60, 0.72, 0.84, 0.92][this.rival.difficulty] ?? 0.70;
      total = Math.round(total * diffMultiplier);
    }
    return total;
  }

  private _rivalEquipped(): Partial<Record<OutfitSlot, string>> {
    const rivalEq: Partial<Record<OutfitSlot, string>> = {};
    for (const id of this.rival.outfitLoadout) { const o = OUTFITS_BY_ID[id]; if (o) rivalEq[o.slot] = id; }
    return rivalEq;
  }

  private _difficultyStars(): string {
    const stars = '★'.repeat(this.rival.difficulty) + '☆'.repeat(5 - this.rival.difficulty);
    return stars;
  }

  private updateScores(): void {
    const myScore = this.calcScore(this.trialEquipped);
    const rivalScore = this.calcScore(this._rivalEquipped(), true);
    this.myScoreText.setText(`로하: ⭐ ${myScore}`);
    this.rivalScoreText.setText(`${this.rival.name}: ⭐ ${rivalScore}`);
  }

  /** Full cinematic battle sequence — ~7 seconds */
  private async battle(): Promise<void> {
    if (this.isBattling) return;
    this.isBattling = true;
    this._safeSfx('click');

    try {
      await this._runBattleSequence();
    } catch (e) {
      console.error('battle sequence failed:', e);
      this.toast.show('⚠️ 오류! 홈으로 돌아갑니다', 'error');
      await this.wait(1000);
      this.abort();
    }
  }

  private async _runBattleSequence(): Promise<void> {
    const myScore = this.calcScore(this.trialEquipped);
    const rivalScore = this.calcScore(this._rivalEquipped(), true);
    const win = myScore >= rivalScore; // 동점도 승리 (초보자 친화)

    // ── 1. Dim overlay + VS intro (2 sec) ─────────────────────────────────
    const overlay = this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT/2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0).setDepth(100);
    this.tweens.add({ targets: overlay, alpha: 0.75, duration: 400 });

    // Stop idle tweens, reset characters to off-screen positions
    if (this.mySprite) { CharacterAnimator.reset(this, this.mySprite); this.mySprite.setX(-200); }
    if (this.rivalSprite) { CharacterAnimator.reset(this, this.rivalSprite); this.rivalSprite.setX(GAME_WIDTH + 200); }

    const vsTitle = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2, '🎬 패션 대결 시작!', {
      fontFamily: 'Pretendard Variable', fontSize: '56px', fontStyle: '900', color: '#ffd95c',
      stroke: '#3a2540', strokeThickness: 6,
    }).setOrigin(0.5).setDepth(110).setAlpha(0).setScale(0.5);

    this.tweens.add({ targets: vsTitle, alpha: 1, scale: 1, duration: 500, ease: 'Back.Out' });
    await this.wait(1800);

    this.tweens.add({ targets: vsTitle, alpha: 0, scale: 1.4, duration: 400, ease: 'Cubic.In',
      onComplete: () => vsTitle.destroy() });
    await this.wait(300);

    // ── 2. Walking phase (3 sec) ───────────────────────────────────────────
    // 캐릭터 sprite가 없어도 진행 가능 (스코어 단계로 점프)
    if (this.mySprite && this.rivalSprite) {
      this.mySprite.setX(-200); this.mySprite.setY(GAME_HEIGHT/2 + 20);
      this.rivalSprite.setX(GAME_WIDTH + 200); this.rivalSprite.setY(GAME_HEIGHT/2 + 20);

      CharacterAnimator.walk(this, this.mySprite);
      CharacterAnimator.walk(this, this.rivalSprite);

      this.tweens.add({ targets: this.mySprite, x: 280, duration: 2000, ease: 'Sine.InOut' });
      this.tweens.add({ targets: this.rivalSprite, x: GAME_WIDTH - 280, duration: 2000, ease: 'Sine.InOut' });
    }

    await this.wait(2200);

    // ── 3. Pose phase — spotlights + scale up (1.5 sec) ────────────────────
    if (this.mySprite) CharacterAnimator.reset(this, this.mySprite);
    if (this.rivalSprite) CharacterAnimator.reset(this, this.rivalSprite);

    // Spotlight circles
    const sL = this.add.circle(280, GAME_HEIGHT/2 + 20, 200, 0xffffff, 0.08).setDepth(99);
    const sR = this.add.circle(GAME_WIDTH-280, GAME_HEIGHT/2 + 20, 200, 0xffffff, 0.08).setDepth(99);
    this.tweens.add({ targets: [sL, sR], alpha: 0.15, duration: 400, yoyo: true, repeat: 1 });

    if (this.mySprite) CharacterAnimator.dance(this, this.mySprite);
    if (this.rivalSprite) CharacterAnimator.dance(this, this.rivalSprite);

    // VS text
    const vsText = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2, 'VS', {
      fontFamily: 'Pretendard Variable', fontSize: '80px', fontStyle: '900', color: '#ff6fa1',
      stroke: '#fff', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(110).setAlpha(0);
    this.tweens.add({ targets: vsText, alpha: 1, scale: 1.2, duration: 300, yoyo: true, repeat: 2,
      onComplete: () => vsText.destroy() });

    await this.wait(1800);

    // ── 4. Score reveal (1 sec) ────────────────────────────────────────────
    if (this.mySprite) CharacterAnimator.reset(this, this.mySprite);
    if (this.rivalSprite) CharacterAnimator.reset(this, this.rivalSprite);
    sL.destroy(); sR.destroy();

    // Animated score count-up
    const myScoreLabel = this.add.text(280, GAME_HEIGHT/2 - 160, `로하: 0점`, {
      fontFamily: 'Pretendard Variable', fontSize: '28px', fontStyle: '900', color: '#ffd95c',
      stroke: '#3a2540', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(120);

    const rScoreLabel = this.add.text(GAME_WIDTH-280, GAME_HEIGHT/2 - 160, `${this.rival.name}: 0점`, {
      fontFamily: 'Pretendard Variable', fontSize: '28px', fontStyle: '900', color: '#ffd95c',
      stroke: '#3a2540', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(120);

    // Count up animation
    const STEPS = 12;
    for (let s = 1; s <= STEPS; s++) {
      await this.wait(60);
      const frac = s / STEPS;
      myScoreLabel.setText(`로하: ${Math.round(myScore * frac)}점`);
      rScoreLabel.setText(`${this.rival.name}: ${Math.round(rivalScore * frac)}점`);
    }

    await this.wait(600);

    // ── 5. Verdict ────────────────────────────────────────────────────────
    overlay.setAlpha(0.5);

    if (win) {
      // Win
      if (this.mySprite) CharacterAnimator.cheer(this, this.mySprite);
      if (this.rivalSprite) CharacterAnimator.sad(this, this.rivalSprite);
      this._safeSfx('win');

      const winText = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 - 60, '✨ 승리!', {
        fontFamily: 'Pretendard Variable', fontSize: '72px', fontStyle: '900', color: '#ffd95c',
        stroke: '#3a2540', strokeThickness: 6,
      }).setOrigin(0.5).setDepth(130).setAlpha(0).setScale(0.3);
      this.tweens.add({ targets: winText, alpha: 1, scale: 1, duration: 500, ease: 'Back.Out' });

      // Confetti particles
      this.spawnConfetti();

      await this.wait(1000);

      const reward = { stars: 30 + this.rival.difficulty * 10, coins: 200 + this.rival.difficulty * 50 };
      EconomyEngine.grant(reward);
      GameState.data.stats.rivalWins++;
      if (this.rival.rewardOutfitId && !GameState.data.ownedOutfits.some(o => o.outfitId === this.rival.rewardOutfitId)) {
        GameState.data.ownedOutfits.push({ outfitId: this.rival.rewardOutfitId, acquiredAt: Date.now() });
        this.toast.show(`🏆 승리! ⭐+${reward.stars}, 💰+${reward.coins} + ${OUTFITS_BY_ID[this.rival.rewardOutfitId]?.name ?? ''} 해금!`, 'gold');
        EventBus.emit('outfit-acquired', this.rival.rewardOutfitId);
      } else {
        this.toast.show(`🏆 승리! ⭐+${reward.stars}, 💰+${reward.coins}`, 'gold');
      }
    } else {
      // Lose
      if (this.mySprite) CharacterAnimator.sad(this, this.mySprite);
      this._safeSfx('lose');

      const loseText = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 - 60, '😢 다음에 다시!', {
        fontFamily: 'Pretendard Variable', fontSize: '52px', fontStyle: '900', color: '#ff6fa1',
        stroke: '#3a2540', strokeThickness: 4,
      }).setOrigin(0.5).setDepth(130).setAlpha(0);
      this.tweens.add({ targets: loseText, alpha: 1, y: loseText.y - 20, duration: 500 });

      this.toast.show(`😢 패배... ${myScore} vs ${rivalScore}. 다시 도전!`);
    }

    GameState.persist();
    await this.wait(2500);
    this.abort();
  }

  /** Spawn confetti particles (simple coloured rects) */
  private spawnConfetti(): void {
    const colors = [0xffd95c, 0xff6fa1, 0x9b6bff, 0x6fd4c4, 0xffffff];
    for (let i = 0; i < 40; i++) {
      const x = Math.random() * GAME_WIDTH;
      const c = this.add.rectangle(x, -20, 12, 12, colors[Math.floor(Math.random() * colors.length)]).setDepth(150);
      this.tweens.add({
        targets: c,
        y: GAME_HEIGHT + 40,
        x: x + (Math.random() - 0.5) * 300,
        angle: Math.random() * 720 - 360,
        duration: 1500 + Math.random() * 1000,
        delay: Math.random() * 600,
        ease: 'Sine.In',
        onComplete: () => c.destroy(),
      });
    }
  }

  /** Promisified delay */
  private wait(ms: number): Promise<void> {
    return new Promise(resolve => this.time.delayedCall(ms, resolve));
  }

  private abort(): void {
    GameState.data.currentEquipped = this.originalEquipped;
    GameState.persist();
    this.cameras.main.fadeOut(300);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.stop('HUDScene');
      this.scene.start('HomeScene');
    });
  }
}
